import { BINDING_ORIGINS } from "../constants/blastConstants";

/**
 * Build email payload from template editor form
 * @param {Object} form - Form values from template editor
 * @returns {Array} Payload array for API
 */
export const buildEmailPayload = (form = {}) => [
  { field: "from", value: form.from || "" },
  { field: "sender_name", value: form.senderName || form.sender_name || "" },
  { field: "to", value: form.to || "{{To}}" },
  { field: "subject", value: form.subject || "" },
  { field: "cc", value: form.cc || "" },
  { field: "bcc", value: form.bcc || "" },
  { field: "body", value: form.body || "" },
  { field: "description", value: form.description || "" },
];

/**
 * Build email bindings from template variables
 * @param {Array} variables - Variables from template editor
 * @param {Array} contactFields - Available contact fields (from CSV or database)
 * @returns {Array} Binding array for API
 */
export const buildEmailBindings = (variables = [], contactFields = []) => {
  const bindings = [];
  const addedNames = new Set();

  // Always include To binding for email recipient
  bindings.push({
    name: "To",
    variable: "{{To}}",
    origin: BINDING_ORIGINS.EVENT,
    value: "email",
  });
  addedNames.add("To");

  // Add bindings for each template variable
  variables.forEach((variable) => {
    if (!variable.name || addedNames.has(variable.name)) return;

    bindings.push({
      name: variable.name,
      variable: `{{${variable.variable || variable.name}}}`,
      origin: variable.origin || BINDING_ORIGINS.EVENT,
      value: variable.origin === BINDING_ORIGINS.FIXED 
        ? variable.value 
        : variable.field || variable.value,
    });
    addedNames.add(variable.name);
  });

  return bindings;
};

/**
 * Build variable list from contact fields for template editor
 * @param {Array} contactFields - Available contact fields
 * @param {string} source - "csv" or "database"
 * @returns {Array} Variables for template editor
 */
export const buildTemplateVariables = (contactFields = [], source = "csv") => {
  const variables = [];

  // Add email variable (always available)
  variables.push({
    name: "Email",
    variable: "Email",
    origin: BINDING_ORIGINS.FIXED,
    value: "hello@example.com",
  });

  // Add To variable for recipient
  variables.push({
    name: "To",
    variable: "To",
    origin: BINDING_ORIGINS.EVENT,
    value: "email",
    source: "contacts",
    field: "email",
  });

  // Add variables for each contact field
  contactFields.forEach((field) => {
    const fieldName = typeof field === "string" ? field : field.name || field.mapping;
    if (!fieldName || fieldName === "email") return;

    variables.push({
      name: fieldName,
      variable: fieldName,
      origin: BINDING_ORIGINS.EVENT,
      value: fieldName,
      source: "contacts",
      field: fieldName,
    });
  });

  return variables;
};

/**
 * Extract available fields from contacts
 * @param {Array} contacts - Contact objects
 * @returns {Array} Available field names
 */
export const extractContactFields = (contacts = []) => {
  if (!contacts.length) return [];

  const fields = new Set();

  contacts.forEach((contact) => {
    Object.keys(contact).forEach((key) => {
      // Exclude internal fields starting with _
      if (!key.startsWith("_") && contact[key]) {
        fields.add(key);
      }
    });
  });

  return Array.from(fields);
};

/**
 * Build email template event source list for template editor
 * @param {Array} fields - Contact field names
 * @returns {Array} Event source list for template editor
 */
export const buildEventSourceList = (fields = []) => {
  return [
    {
      name: "contacts",
      origin: "contacts",
      fields: fields.map((field) => ({
        name: field,
        field: field,
      })),
    },
  ];
};
